// File: frontend/src/components/Settings/SystemSettings.jsx
// System and company settings component

import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  BuildingOfficeIcon,
  GlobeAltIcon,
  CurrencyDollarIcon,
  ClockIcon,
  LanguageIcon,
  CheckCircleIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';
import { getSystemSettings, updateSystemSetting } from '../../services/api';
import LoadingSpinner from '../UI/LoadingSpinner';

const SystemSettings = ({ filter = null }) => {
  const [editingSettings, setEditingSettings] = useState({});
  const [tempValues, setTempValues] = useState({});
  const [notification, setNotification] = useState(null);
  
  const queryClient = useQueryClient();

  // Get system settings
  const { data: settingsData, isLoading, error } = useQuery({
    queryKey: ['systemSettings'],
    queryFn: getSystemSettings
  });

  // Update setting mutation
  const updateMutation = useMutation({
    mutationFn: ({ key, value }) => updateSystemSetting(key, value),
    onSuccess: () => {
      queryClient.invalidateQueries(['systemSettings']);
      setNotification({
        type: 'success',
        message: 'Configuração atualizada com sucesso'
      });
      setTimeout(() => setNotification(null), 3000);
    },
    onError: (error) => {
      setNotification({
        type: 'error',
        message: error.response?.data?.message || 'Erro ao atualizar configuração'
      });
      setTimeout(() => setNotification(null), 3000);
    }
  });

  // Initialize temp values when settings load
  useEffect(() => {
    if (settingsData?.data?.settings) {
      const allSettings = Object.values(settingsData.data.settings).flat();
      const values = {};
      allSettings.forEach(setting => {
        values[setting.setting_key] = setting.setting_value;
      });
      setTempValues(values);
    }
  }, [settingsData]);

  const handleEdit = (key) => {
    setEditingSettings(prev => ({ ...prev, [key]: true }));
  };

  const handleSave = (key) => {
    updateMutation.mutate({
      key,
      value: tempValues[key]
    });
    setEditingSettings(prev => ({ ...prev, [key]: false }));
  };

  const handleCancel = (key, originalValue) => {
    setTempValues(prev => ({ ...prev, [key]: originalValue }));
    setEditingSettings(prev => ({ ...prev, [key]: false }));
  };

  const handleInputChange = (key, value) => {
    setTempValues(prev => ({ ...prev, [key]: value }));
  };

  const renderSettingField = (setting) => {
    const key = setting.setting_key;
    const isEditing = editingSettings[key];
    const currentValue = tempValues[key] || setting.setting_value;

    // Determine input type based on setting type and key
    const getInputType = () => {
      if (setting.setting_type === 'boolean') return 'checkbox';
      if (key.includes('email')) return 'email';
      if (key.includes('phone')) return 'tel';
      if (key.includes('website') || key.includes('url')) return 'url';
      return 'text';
    };

    const getInputConfig = () => {
      const inputType = getInputType();
      
      if (inputType === 'checkbox') {
        return {
          type: 'checkbox',
          checked: currentValue === 'true' || currentValue === true,
          onChange: (e) => handleInputChange(key, e.target.checked.toString())
        };
      }

      // Special handling for select fields
      if (key === 'crm_timezone') {
        return {
          type: 'select',
          value: currentValue,
          onChange: (e) => handleInputChange(key, e.target.value),
          options: [
            { value: 'America/Sao_Paulo', label: 'São Paulo (GMT-3)' },
            { value: 'America/New_York', label: 'New York (EST)' },
            { value: 'Europe/London', label: 'London (GMT)' },
            { value: 'Europe/Madrid', label: 'Madrid (CET)' }
          ]
        };
      }

      if (key === 'crm_currency') {
        return {
          type: 'select',
          value: currentValue,
          onChange: (e) => handleInputChange(key, e.target.value),
          options: [
            { value: 'BRL', label: 'Real Brasileiro (R$)' },
            { value: 'USD', label: 'US Dollar ($)' },
            { value: 'EUR', label: 'Euro (€)' },
            { value: 'GBP', label: 'British Pound (£)' }
          ]
        };
      }

      if (key === 'crm_language') {
        return {
          type: 'select',
          value: currentValue,
          onChange: (e) => handleInputChange(key, e.target.value),
          options: [
            { value: 'pt-BR', label: 'Português (Brasil)' },
            { value: 'en-US', label: 'English (US)' },
            { value: 'es-ES', label: 'Español' },
            { value: 'fr-FR', label: 'Français' }
          ]
        };
      }

      return {
        type: inputType,
        value: currentValue,
        onChange: (e) => handleInputChange(key, e.target.value)
      };
    };

    const inputConfig = getInputConfig();

    return (
      <div key={key} className="py-4 border-b border-gray-200 dark:border-gray-700 last:border-b-0">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <dt className="text-sm font-medium text-gray-900 dark:text-gray-100 capitalize">
              {key.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1')}
            </dt>
            <dd className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {setting.description}
            </dd>
          </div>
          
          <div className="ml-4 flex items-center space-x-2">
            {isEditing ? (
              <div className="flex items-center space-x-2">
                <div className="w-48">
                  {inputConfig.type === 'checkbox' ? (
                    <input
                      type="checkbox"
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      {...inputConfig}
                    />
                  ) : inputConfig.type === 'select' ? (
                    <select
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      {...inputConfig}
                    >
                      {inputConfig.options.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      {...inputConfig}
                    />
                  )}
                </div>
                <button
                  onClick={() => handleSave(key)}
                  disabled={updateMutation.isPending}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  {updateMutation.isPending ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    <CheckCircleIcon className="h-4 w-4" />
                  )}
                </button>
                <button
                  onClick={() => handleCancel(key, setting.setting_value)}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Cancelar
                </button>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <dd className="text-sm text-gray-900 dark:text-gray-100">
                  {setting.setting_type === 'boolean' 
                    ? (currentValue === 'true' ? 'Activado' : 'Desactivado')
                    : currentValue
                  }
                </dd>
                <button
                  onClick={() => handleEdit(key)}
                  className="text-indigo-600 hover:text-indigo-900 text-sm font-medium"
                >
                  Editar
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <LoadingSpinner />
        <p className="text-center text-gray-600 dark:text-gray-300 mt-4">Carregando configurações...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <ExclamationCircleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Erro ao carregar configurações
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error.message}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const settings = settingsData?.data?.settings || {};
  const categoriesToShow = filter ? { [filter]: settings[filter] || [] } : settings;

  return (
    <div className="p-6">
      {/* Notification */}
      {notification && (
        <div className={`mb-6 rounded-md p-4 ${
          notification.type === 'success' ? 'bg-green-50' : 'bg-red-50'
        }`}>
          <div className="flex">
            {notification.type === 'success' ? (
              <CheckCircleIcon className="h-5 w-5 text-green-400" />
            ) : (
              <ExclamationCircleIcon className="h-5 w-5 text-red-400" />
            )}
            <div className="ml-3">
              <p className={`text-sm font-medium ${
                notification.type === 'success' ? 'text-green-800' : 'text-red-800'
              }`}>
                {notification.message}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Settings Groups */}
      {Object.entries(categoriesToShow).map(([category, categorySettings]) => (
        <div key={category} className="mb-8">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4 capitalize flex items-center">
            {category === 'company' && <BuildingOfficeIcon className="h-5 w-5 mr-2" />}
            {category === 'crm' && <GlobeAltIcon className="h-5 w-5 mr-2" />}
            {category === 'notifications' && <CheckCircleIcon className="h-5 w-5 mr-2" />}
            {category.replace(/_/g, ' ')}
          </h3>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <dl className="px-6 py-4">
              {categorySettings.map(renderSettingField)}
            </dl>
          </div>
        </div>
      ))}

      {Object.keys(categoriesToShow).length === 0 && (
        <div className="text-center py-12">
          <CurrencyDollarIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">Não há configurações</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Não foram encontradas configurações para exibir.
          </p>
        </div>
      )}
    </div>
  );
};

export default SystemSettings;